import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useStore, Deal, ContactLog } from '@/store/useStore';
import { dealToDbLead } from '@/modules/crm/hooks/useSupabaseData';
import { toast } from 'sonner';
import { useAuditLog } from '@/hooks/useAuditLog';
import { queryClient } from '@/App';
import { queryKeys } from '@/lib/queryKeys';
import { captureError } from '@/lib/sentry';
import { LeadSaveError, toLeadSaveError } from '@/modules/crm/lib/leadSaveError';

/**
 * Forma do UPDATE na tabela `leads`, tirada do schema gerado.
 * Antes era Record<string, unknown>, que o cliente do Supabase recusa: ele
 * checa propriedades em excesso justamente para pegar nome de coluna errado
 * — e foi assim que colunas inexistentes passaram despercebidas em outros
 * módulos deste repo.
 */
type LeadUpdate = Database['public']['Tables']['leads']['Update'];

/**
 * Converte um valor da aplicação para a forma que uma coluna jsonb aceita.
 * `ContactLog[]` e os campos personalizados são objetos nossos: estruturas
 * serializáveis, mas sem a assinatura de índice que o tipo `Json` exige.
 * O cast é seguro porque o valor vai direto para JSON.stringify no driver.
 */
const toJson = (v: unknown) => v as LeadUpdate['contact_logs'];

// Cache do usuário atual para evitar múltiplos getUser()
let _cachedUser: { id: string; name: string } | null = null;
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT' || event === 'USER_UPDATED') _cachedUser = null;
});
async function getCurrentUser() {
  if (_cachedUser) return _cachedUser;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data: prof } = await supabase.from('profiles').select('name').eq('id', user.id).maybeSingle();
  _cachedUser = { id: user.id, name: prof?.name || 'Usuário' };
  return _cachedUser;
}

// Utilitário para inferir território via DDD
const dddToState: Record<string, string> = {
  '11': 'SP', '12': 'SP', '13': 'SP', '14': 'SP', '15': 'SP', '16': 'SP', '17': 'SP', '18': 'SP', '19': 'SP',
  '21': 'RJ', '22': 'RJ', '24': 'RJ', '27': 'ES', '28': 'ES', '31': 'MG', '32': 'MG', '33': 'MG', '34': 'MG',
  '35': 'MG', '37': 'MG', '38': 'MG', '41': 'PR', '42': 'PR', '43': 'PR', '44': 'PR', '45': 'PR', '46': 'PR',
  '47': 'SC', '48': 'SC', '49': 'SC', '51': 'RS', '53': 'RS', '54': 'RS', '55': 'RS', '61': 'DF', '62': 'GO',
  '64': 'GO', '63': 'TO', '65': 'MT', '66': 'MT', '67': 'MS', '68': 'AC', '69': 'RO', '71': 'BA', '73': 'BA',
  '74': 'BA', '75': 'BA', '77': 'BA', '79': 'SE', '81': 'PE', '82': 'AL', '83': 'PB', '84': 'RN', '85': 'CE',
  '86': 'PI', '88': 'CE', '89': 'PI', '91': 'PA', '92': 'AM', '93': 'PA', '94': 'PA', '95': 'RR', '96': 'AP',
  '97': 'AM', '98': 'MA', '99': 'MA'
};

function inferStateFromPhone(phone?: string, currentTerritory?: string) {
  if (currentTerritory && currentTerritory.trim() !== '') return currentTerritory;
  if (!phone) return currentTerritory;
  
  // Extrai apenas números
  const numbers = phone.replace(/\D/g, '');
  
  // Se tiver código de país (ex: 5511999999999)
  if (numbers.startsWith('55') && numbers.length >= 12) {
    const ddd = numbers.substring(2, 4);
    return dddToState[ddd] || currentTerritory;
  }
  
  // Se começar direto com DDD (ex: 11999999999)
  if (numbers.length >= 10 && numbers.length <= 11) {
    const ddd = numbers.substring(0, 2);
    return dddToState[ddd] || currentTerritory;
  }

  return currentTerritory;
}

/**
 * Unified hook for all lead/deal mutations.
 * Handles both Zustand store updates AND database persistence.
 */
/** Maior data+hora (local) dos logs em ISO, ou null — espelha o que o banco grava em last_contact_at. */
function latestContactIso(logs: ContactLog[]): string | null {
  let best = 0;
  for (const l of logs) {
    if (!l?.date) continue;
    const t = new Date(`${l.date}T${l.time || '00:00'}`).getTime();
    if (Number.isFinite(t) && t > best) best = t;
  }
  return best ? new Date(best).toISOString() : null;
}

export function useLeadMutations() {
  const activeProjectId = useStore(s => s.activeProjectId);
  const { log: auditLog } = useAuditLog();

  // ── Uma fonte de verdade: o cache do React Query (`queryKeys.deals`) ──
  // As mutações escrevem SÓ aqui; `store.deals` é um espelho preenchido pelo
  // DbSync (para Foco do Dia, InsightNotifier, diálogos antigos). Antes cada
  // mutação escrevia nos dois e qualquer tela que lesse a cópia "errada" via
  // dado velho — foi a família de bugs de 14/09.
  const patchCache = useCallback((fn: (old: Deal[]) => Deal[]) => {
    if (!activeProjectId) return;
    queryClient.setQueryData<Deal[]>(queryKeys.deals(activeProjectId), (old) => (old ? fn(old) : old));
  }, [activeProjectId]);
  const getDeal = useCallback((id: string): Deal | undefined => (
    queryClient.getQueryData<Deal[]>(queryKeys.deals(activeProjectId))?.find(d => d.id === id)
  ), [activeProjectId]);
  const refetchDeals = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) });
  }, [activeProjectId]);

  // ✅ Fix #3: Optimistic updates - sem triggerDbSync redundante
  //
  // Falha vira EXCEÇÃO (`LeadSaveError`), não toast genérico: quem chama é
  // que sabe o que fazer com o formulário (manter aberto, apontar o lead
  // duplicado). Antes, o erro era engolido aqui e o dialog seguia mostrando
  // "adicionado" — o lead sumia segundos depois, no rollback.
  const createLead = useCallback(async (deal: Omit<Deal, 'id'> & { id?: string }) => {
    const id = deal.id || crypto.randomUUID();

    // Sem projeto ativo o INSERT nunca acontece. Guardar só no store parecia
    // sucesso e evaporava no próximo reload.
    if (!activeProjectId) {
      throw new LeadSaveError('Nenhum projeto selecionado. Escolha um projeto antes de criar o lead.', 'no_project');
    }
    
    // Infer território pelo DDD se estiver vazio
    const inferredTerritory = inferStateFromPhone(deal.phone, deal.territory);
    
    const fullDeal: Deal = { ...deal, id, territory: inferredTerritory } as Deal;
    // Optimistic: entra no cache já (o board ordena por created_at desc).
    patchCache(old => [fullDeal, ...old]);

    const dbLead = dealToDbLead(fullDeal);
    // ✅ Fix: include id so Supabase uses the same UUID as the optimistic deal,
    // preventing duplicates from realtime INSERT events
    const { error } = await supabase.from('leads').insert({ id, ...dbLead, project_id: activeProjectId } as Database['public']['Tables']['leads']['Insert']);
    if (error) {
      // Rollback
      patchCache(old => old.filter(d => d.id !== id));
      const saveError = toLeadSaveError(error, fullDeal);
      captureError(saveError, { pgCode: error.code, pgMessage: error.message, projectId: activeProjectId });
      throw saveError;
    }
    // Removido triggerDbSync() - realtime já atualiza outros clientes
    auditLog('lead_created', 'lead', id, { company: fullDeal.company, contact: fullDeal.contact });
    return fullDeal;
  }, [activeProjectId, patchCache, auditLog]);

  const createLeadsBulk = useCallback(async (deals: Deal[]) => {
    // Optimistic: adiciona imediatamente iterando pra inferir DDD
    const processedDeals = deals.map(d => ({
      ...d,
      territory: inferStateFromPhone(d.phone, d.territory)
    }));
    
    patchCache(old => [...processedDeals, ...old]);
    if (activeProjectId) {
      const dbRows = processedDeals.map(l => ({ id: l.id, ...dealToDbLead(l), project_id: activeProjectId }));
      const { error } = await supabase.from('leads').insert(dbRows as Database['public']['Tables']['leads']['Insert'][]);
      if (error) {
        toast.error('Erro ao importar leads');
        // Rollback
        const ids = new Set(deals.map(d => d.id));
        patchCache(old => old.filter(d => !ids.has(d.id)));
      }
      // Removido triggerDbSync() - realtime propaga
      auditLog('data_imported', 'lead', undefined, { count: deals.length });
    }
  }, [activeProjectId, patchCache, auditLog]);

  const persistUpdate = useCallback(async (id: string, updates: LeadUpdate) => {
    const { error } = await supabase.from('leads').update(updates).eq('id', id);
    if (error) {
      toast.error('Erro ao atualizar lead');
    }
  }, []);

  const saveDeal = useCallback(async (deal: Deal) => {
    const inferredTerritory = inferStateFromPhone(deal.phone, deal.territory);
    const dealToSave = { ...deal, territory: inferredTerritory };

    // Capture old deal BEFORE optimistic update for field-level diff
    const oldDeal = queryClient.getQueryData<Deal[]>(queryKeys.deals(activeProjectId))?.find(d => d.id === dealToSave.id);

    // Optimistic
    patchCache(old => old.map(d => d.id === dealToSave.id ? dealToSave : d));
    const fullDbData = dealToDbLead(dealToSave);
    // Always exclude trigger-managed fields
    delete fullDbData.column_id;
    delete fullDbData.status_history;
    delete fullDbData.timeline;
    delete fullDbData.days_in_column;
    // If we have old data, only send fields that actually changed
    let dbData: LeadUpdate = fullDbData;
    if (oldDeal) {
      const oldDbData = dealToDbLead(oldDeal);
      const diff: LeadUpdate = {};
      for (const [key, val] of Object.entries(fullDbData)) {
        if (JSON.stringify(val) !== JSON.stringify(oldDbData[key])) (diff as Record<string, unknown>)[key] = val;
      }
      if (Object.keys(diff).length === 0) return; // No changes
      dbData = diff;
    }
    const { error } = await supabase.from('leads').update(dbData).eq('id', dealToSave.id);
    if (error) {
      toast.error('Erro ao atualizar lead');
      // Rollback: invalidate cache to refetch from DB
      queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) });
    }
    auditLog('lead_updated', 'lead', deal.id, { company: deal.company });
  }, [activeProjectId, patchCache, auditLog]);

  const moveToColumn = useCallback(async (id: string, toColumn: string) => {
    // Optimistic: o mesmo que o trigger `trg_track_lead_column_change` vai
    // gravar (histórico, timeline, dias na coluna) — o refetch depois só confirma.
    const today = new Date().toISOString().split('T')[0];
    const { kanbanColumns, userProfile } = useStore.getState();
    const userName = userProfile?.name || 'Usuário';
    const colLabel = kanbanColumns.find(c => c.id === toColumn)?.label || toColumn;
    patchCache(old => old.map(d => d.id === id ? {
      ...d,
      column: toColumn,
      daysInColumn: 0,
      lastStatusDate: today,
      statusHistory: [...(d.statusHistory || []), { from: d.column, to: toColumn, date: today, user: userName }],
      timeline: [...(d.timeline || []), { date: today, action: `Movido para ${colLabel}`, user: userName }],
    } : d));

    // Persist to database. The postgres trigger `trg_track_lead_column_change`
    // will atomically handle status_history, timeline, days_in_column, and last_status_date appends!
    const { error } = await supabase.from('leads').update({ column_id: toColumn }).eq('id', id);
    if (error) {
      toast.error('Erro ao mover lead');
      queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) });
    }
    auditLog('lead_moved', 'lead', id, { to: toColumn });
    return toColumn; // Return column for conversion dialog check
  }, [activeProjectId, patchCache, auditLog]);

  // Archive a lead (for conversion) — does NOT show in trash, uses column 'converted'
  const archiveDeal = useCallback(async (id: string) => {
    if (activeProjectId) {
      queryClient.setQueryData<Deal[]>(queryKeys.deals(activeProjectId), (old) => {
        if (!old) return old;
        return old.filter(d => d.id !== id);
      });
    }
    // Mark as converted, not deleted — won't appear in trash
    const { error } = await supabase.from('leads').update({ column_id: 'converted', status: 'converted' }).eq('id', id);
    if (error) { toast.error('Erro ao arquivar lead'); queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) }); }
    auditLog('lead_converted', 'lead', id);
  }, [activeProjectId, auditLog]);

  const removeDeal = useCallback(async (id: string) => {
    if (activeProjectId) {
      queryClient.setQueryData<Deal[]>(queryKeys.deals(activeProjectId), (old) => {
        if (!old) return old;
        return old.filter(d => d.id !== id);
      });
    }
    const { error } = await supabase.from('leads').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast.error('Erro ao excluir lead'); queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) }); }
    auditLog('lead_deleted', 'lead', id);
  }, [activeProjectId, auditLog]);

  const removeDeals = useCallback(async (ids: string[]) => {
    if (activeProjectId) {
      queryClient.setQueryData<Deal[]>(queryKeys.deals(activeProjectId), (old) => {
        if (!old) return old;
        return old.filter(d => !ids.includes(d.id));
      });
    }
    // Em lotes: `id=in.(…)` vai na URL e ~200 UUIDs já estouram os 8 KB do
    // Kong/nginx (414) — excluir uma seleção grande falhava inteira.
    const deletedAt = new Date().toISOString();
    let error: unknown = null;
    for (let i = 0; i < ids.length; i += 150) {
      const res = await supabase.from('leads').update({ deleted_at: deletedAt }).in('id', ids.slice(i, i + 150));
      if (res.error) { error = res.error; break; }
    }
    if (error) { toast.error('Erro ao excluir leads'); queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) }); }
    auditLog('bulk_delete', 'lead', undefined, { count: ids.length });
  }, [activeProjectId, auditLog]);

  /**
   * Bulk-tag selected leads. `mode = 'add'` merges the tags into each lead's
   * existing set; `mode = 'remove'` strips them. We can't do this in a single
   * UPDATE because each row's `tags` array is different, so we issue per-row
   * updates in parallel after computing the new array client-side.
   *
   * Optimistically updates both Zustand and React Query, rolling back on
   * the first error. Per-row failures still bubble a toast.
   */
  const setTagsBulk = useCallback(async (ids: string[], tags: string[], mode: 'add' | 'remove' = 'add') => {
    if (ids.length === 0 || tags.length === 0) return;
    const allDeals = queryClient.getQueryData<Deal[]>(queryKeys.deals(activeProjectId)) || [];
    const newTagsByDealId = new Map<string, string[]>();
    for (const id of ids) {
      const d = allDeals.find(x => x.id === id);
      if (!d) continue;
      const cur = new Set(d.tags || []);
      if (mode === 'add') tags.forEach(t => cur.add(t));
      else tags.forEach(t => cur.delete(t));
      newTagsByDealId.set(id, Array.from(cur));
    }

    // Optimistic
    patchCache(old => old.map(d => newTagsByDealId.has(d.id) ? { ...d, tags: newTagsByDealId.get(d.id)! } : d));

    // Persist — parallel per-row UPDATE since each row has a unique tags array.
    const results = await Promise.all(
      Array.from(newTagsByDealId.entries()).map(([id, newTags]) =>
        supabase.from('leads').update({ tags: newTags }).eq('id', id)
      )
    );
    const failed = results.filter(r => r.error).length;
    if (failed > 0) {
      toast.error(`${failed} lead${failed > 1 ? 's' : ''} não atualizado${failed > 1 ? 's' : ''}`);
      // Rollback by invalidating
      queryClient.invalidateQueries({ queryKey: queryKeys.deals(activeProjectId) });
    }
    auditLog(mode === 'add' ? 'bulk_tag_add' : 'bulk_tag_remove', 'lead', undefined, { count: ids.length, tags });
  }, [activeProjectId, patchCache, auditLog]);

  const saveContactLog = useCallback(async (dealId: string, log: ContactLog) => {
    const deal = getDeal(dealId);
    if (!deal) return undefined;
    const logs = [...(deal.contactLogs || []), log];
    // Otimista: o banco recalcula last_contact_at pelo trigger; aqui só
    // antecipamos o valor para o filtro/Foco do Dia não piscarem.
    const updated: Deal = { ...deal, contactLogs: logs, lastContactAt: latestContactIso(logs) };
    patchCache(old => old.map(d => d.id === dealId ? updated : d));
    await persistUpdate(dealId, { contact_logs: toJson(updated.contactLogs) });
    return updated;
  }, [getDeal, patchCache, persistUpdate]);

  const removeContactLog = useCallback(async (dealId: string, logId: string) => {
    const deal = getDeal(dealId);
    if (!deal) return undefined;
    const logs = (deal.contactLogs || []).filter(l => l.id !== logId);
    const updated: Deal = { ...deal, contactLogs: logs, lastContactAt: latestContactIso(logs) };
    patchCache(old => old.map(d => d.id === dealId ? updated : d));
    await persistUpdate(dealId, { contact_logs: toJson(updated.contactLogs) });
    return updated;
  }, [getDeal, patchCache, persistUpdate]);

  const saveCustomField = useCallback(async (dealId: string, fieldId: string, value: unknown) => {
    const deal = getDeal(dealId);
    if (!deal) return null;
    const updated = { ...deal, customFields: { ...deal.customFields, [fieldId]: value } };
    patchCache(old => old.map(d => d.id === dealId ? updated : d));
    await persistUpdate(dealId, { custom_fields: toJson(updated.customFields) });
    return updated;
  }, [getDeal, patchCache, persistUpdate]);

  return {
    createLead,
    createLeadsBulk,
    saveDeal,
    moveToColumn,
    removeDeal,
    removeDeals,
    setTagsBulk,
    archiveDeal,
    saveContactLog,
    removeContactLog,
    saveCustomField,
    persistUpdate,
    /** Lead como está no cache agora (pós-mutação). */
    getDeal,
    /** Ajuste local no cache (ex.: esconder um lead durante o "desfazer" da exclusão). */
    patchDeals: patchCache,
    refetchDeals,
  };
}
